import "./Common.css";
import "./Bqms.css";

function Bqms() {
  const bqmsDashbordVid =
    "https://1drv.ms/v/c/c94efc88b6ce9f63/IQTjVXQlijOvSKY5A5OK__g2AYeyJShDGCarnCXqp9-xZSg";
  const bqmsCusDashbordVid =
    "https://1drv.ms/v/c/c94efc88b6ce9f63/IQSdM-1Z5JzoTaS-Jst9AqY_AUXppWHjA2PuxJBHiPLSoPw";
  const bqmsCircuitDiagram =
    "https://1drv.ms/i/c/c94efc88b6ce9f63/IQQdPiO5UNVZTIHUVZ5g_VkZAel2V-DaviVT2rMRpUzN5pk";

  const openImageInNewWindow = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderFigure = (url, alt, caption) => (
    <figure className="media-figure">
      <div className="media-figure__visual">
        <img src={url} alt={alt} loading="lazy" />
        <button
          type="button"
          className="view-full-button"
          onClick={() => openImageInNewWindow(url)}
        >
          View full image
        </button>
      </div>
      {caption ? (
        <figcaption className="figcaption-text">{caption}</figcaption>
      ) : null}
    </figure>
  );

  return (
    <main className="content">
      <section className="section bqms">
        <h1 className="text-head">Bank Queue Management System</h1>
        <p className="project-text">
          This project introduces an Integrated Smart Bank Management System
          designed to modernize traditional queuing by offering a multi-channel
          reservation experience. By leveraging the MERN stack for a robust web
          portal and a custom-built IoT hardware gateway for automated
          telephony, the system allows customers to book appointments via the
          web or a dedicated hotline. A dynamic Queue Management Engine serves
          as the core logic, intelligently calculating appointment slots based
          on real-time counter availability, average service handling times, and
          customer priority, ensuring a significant reduction in wait times and
          operational bottlenecks.
        </p>
        <br />
        <p className="project-text">
          On the technical front, the system features a sophisticated Automated
          Call Handling (IVR) unit engineered with an ESP32 microcontroller and
          a SIM800L GSM module. This hardware interface interacts directly with
          the backend via WiFi to process National ID inputs and service
          selections made through a caller's keypad. By overcoming complex
          engineering challenges such as implementing hardware-level DTMF
          decoding for 100% input accuracy and designing a custom power
          regulation circuit for SIM module stability the project demonstrates a
          reliable fusion of software and hardware aimed at enhancing service
          efficiency in the banking sector.
        </p>
        <br />
        <br />
        <br />

        <h1 className="text-head">Core Functionalities</h1>
        <ol className="project-list">
          <li className="project-text">
            <strong>User Interface & Accessibility</strong>
            <ul>
              <li>
                <strong>Web Portal:</strong> Users can log in via their National
                Identity Card (NIC) number to book specific banking services for
                a preferred date.
              </li>
              <li>
                <strong>Automated Telephony:</strong>A dedicated hotline allows
                users to make reservations via voice calls, inputting data
                through their phone keypad.
              </li>
              <li>
                <strong>Notifications:</strong> Automated SMS confirmations are
                sent for all successful bookings, whether made via web or phone.
              </li>
            </ul>
          </li>
          <figure>
            <div className="iframe-container">
              <iframe
                className="bqms-iframe"
                src={bqmsCusDashbordVid}
                title="Bank Queue Management System Dashboard"
                allow="accelerometer; encrypted-media; gyroscope"
                allowFullScreen
              ></iframe>
            </div>
            <figcaption className="figcaption-text">
              A video demonstration of the Customer dashboard.
            </figcaption>
          </figure>
          <br />
          <br />

          <li className="project-text">
            <strong>Administrative & Resource Management</strong>
            <ul>
              <li>
                <strong>Service Management:</strong> Administrators can define
                new services, setting an Average Handling Time (AHT) for each to
                optimize scheduling.
              </li>
              <li>
                <strong>Counter Management:</strong> Ability to dynamically
                assign specific services to different bank counters.
              </li>
              <li>
                <strong>Communication Logs:</strong> A centralized dashboard to
                monitor call and SMS history.
              </li>
              <li>
                <strong>Security:</strong> Role-based access control with secure
                credential management for counter staff.
              </li>
            </ul>
          </li>
          <figure>
            <div className="iframe-container">
              <iframe
                className="bqms-iframe"
                src={bqmsDashbordVid}
                title="Bank Queue Management System Dashboard"
                allow="accelerometer; encrypted-media; gyroscope"
                allowFullScreen
              ></iframe>
            </div>
            <figcaption className="figcaption-text">
              A video demonstration of the web dashboard.
            </figcaption>
          </figure>
          <br />

          <li className="project-text">
            <strong>Advanced Queue Engine</strong>
            <ul>
              <li>
                <strong>Efficiency Overrides:</strong> Counter staff can trigger
                a "Next Customer" signal upon completion; the system then calls
                the next person immediately without waiting for the
                pre-allocated slot to expire.
              </li>
              <li>
                <strong>Dynamic Delays:</strong> If a transaction exceeds the
                average time, the system maintains the current status until the
                staff member confirms completion.
              </li>
              <li>
                <strong>Multi-Service Logic:</strong> If a customer requires two
                services, the system prioritizes them based on customer
                preference and routes them to the subsequent counter.
              </li>
              <li>
                <strong>Load Balancing:</strong> If multiple counters offer the
                same service, the system distributes customers based on
                real-time traffic and current bookings.
              </li>
            </ul>
          </li>
        </ol>
        <br />
        <br />

        <h1 className="text-head">Hardware Architecture</h1>
        <div className="hardware-arch">
          <div className="hardware-arch-left">
            <p className="project-text">
              To facilitate the "Call Management" feature, a custom hardware
              gateway was developed to interface with the backend. The SIM800L
              auto-answers incoming calls. The ESP32 fetches voice prompts
              (e.g., "Enter your ID") from the SD card, routing the audio
              through the PCM5102 DAC into the SIM module's Mic input. The
              caller’s keypad responses are captured from the SIM module's audio
              output by the DTMF decoder, which sends a 4-bit signal to the
              ESP32. Data is then synced to the MERN backend via In-built WiFi.
            </p>

            <div className="component-table">
              <table
                border="1"
                cellpadding="8"
                cellspacing="0"
                className="project-text"
                style={{ width: "100%", borderCollapse: "collapse" }}
              >
                <thead style={{ backgroundColor: "#5e93cf70" }}>
                  <tr>
                    <th>Component</th>
                    <th>Function</th>
                    <th>Use Case</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>ESP32</td>
                    <td>Microcontroller Unit</td>
                    <td>Logic & WiFi connectivity to Backend</td>
                  </tr>
                  <tr>
                    <td>SIM800L</td>
                    <td>GSM Module</td>
                    <td>Handles voice calls and SMS</td>
                  </tr>
                  <tr>
                    <td>DTMF Module</td>
                    <td>DTMF Decoder</td>
                    <td>Decodes DTMF signals from customer input</td>
                  </tr>
                  <tr>
                    <td>PCM5102 DAC</td>
                    <td>Digital-to-Analog Converter</td>
                    <td>
                      Converts digital audio signals to analog for SIM800L input
                    </td>
                  </tr>
                  <tr>
                    <td>SD Card Module</td>
                    <td>Storage</td>
                    <td>Stores audio files and log files</td>
                  </tr>
                  <tr>
                    <td>0.96" OLED</td>
                    <td>Display</td>
                    <td>Real-time system status display</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="hardware-arch-right">
            {renderFigure(
              bqmsCircuitDiagram,
              " ",
              "Circuit diagram of the Call Management System.",
            )}
          </div>
        </div>
        <br />
        <br />

        <h1 className="text-head">Technical Stack</h1>
        <ul className="project-list">
          <li className="project-text">
            <strong>Frontend/Backend:</strong> MongoDB, Express.js, React.js,
            Node.js (MERN).
          </li>
          <li className="project-text">
            <strong>Hardware:</strong> ESP32 (Microcontroller), SIM800L
            (GSM/GPRS), PCM5102 (DAC), DTMF Decoder.
          </li>
          <li className="project-text">
            <strong>Communication Protocols:</strong> HTTP/REST, WiFi, SMS, IVR, I2C, I2S, UART, SPI.
          </li>
        </ul>
        <br />
        <br />

        <h1 className="text-head">Engineering Challenges & Solutions</h1>
        <dl className="project-list">
          <dt className="project-text">
            <strong>Challenge 1: </strong> SIM Module Power Instability
          </dt>
          <dd>
            <ul className="project-list">
              <li className="project-text">
                <strong>Issue:</strong> The SIM800L frequently restarted during
                call initialization. This was due to high current spikes and the
                module's sensitivity to voltage (requires ~4.0V, while 3.3V is
                insufficient).
              </li>
              <li className="project-text">
                <strong>Solution:</strong> Integrated a 2200µF capacitor in
                parallel to the module to handle current surges. To achieve the
                ideal voltage from a 5V USB source, a diode (0.7V drop) was
                connected in series to the positive terminal, providing a stable
                4.3V, which falls within the module's safe operating range.
              </li>
            </ul>
          </dd>
          <dt className="project-text">
            <strong>Challenge 2: </strong> Inaccurate DTMF Detection
          </dt>
          <dd>
            <ul className="project-list">
              <li className="project-text">
                <strong>Issue:</strong> The SIM800L's internal firmware-based
                DTMF detection was unreliable and failed to capture inputs
                accurately.
              </li>
              <li className="project-text">
                <strong>Solution:</strong> Bypassed the internal software
                detection by implementing a dedicated external DTMF hardware
                module. This provided a stable 4-bit output, ensuring 100%
                accuracy in capturing user inputs.
              </li>
            </ul>
          </dd>
        </dl>
        <br />
        <br />

        <h1 className="text-head">Conclusion</h1>
        <p className="project-text">
          In conclusion, this project successfully demonstrates a seamless
          integration of <strong>Full-Stack Web Development</strong> and{" "}
          <strong>IoT Hardware Engineering </strong>
          to solve a real-world banking challenge. By providing a dual-access
          reservation system (Web and Automated Call), the solution ensures
          inclusivity for all user types.
        </p>
        <p className="project-text">
          The implementation of a dynamic <strong>Queue Management algorithm </strong>
          significantly reduces customer wait times and optimizes bank resource
          allocation. Furthermore, overcoming critical hardware challenges—such
          as power stability and DTMF accuracy—highlights a robust engineering
          approach to building reliable, high-performance systems. This project
          serves as a scalable model for modernizing service-based industries
          through automation and real-time data synchronization.
        </p>
      </section>
    </main>
  );
}

export default Bqms;
